import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Calendar, User } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ConseilsPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'Tous' },
    { id: 'conseils', name: 'Conseils' },
    { id: 'tendances', name: 'Tendances' },
    { id: 'histoire', name: 'Histoire' },
    { id: 'guide', name: 'Guides' },
  ];

  useEffect(() => {
    fetchArticles();
  }, [selectedCategory]);

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const url = selectedCategory === 'all' ? `${API}/articles` : `${API}/articles?category=${selectedCategory}`;
      const { data } = await axios.get(url);
      setArticles(data);
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="min-h-screen bg-[#FDFAF4] pb-16 relative overflow-hidden">
      {/* Rose filigrane en arrière-plan du header */}
      <img
        src="/assets/rose-filigrane.jpg"
        alt=""
        aria-hidden="true"
        className="absolute top-8 right-8 w-[500px] h-[500px] object-contain pointer-events-none select-none mix-blend-multiply opacity-[0.06] hidden md:block"
      />
      <div className="max-w-7xl mx-auto px-6 py-16 relative z-10">
        <h1
          className="text-5xl md:text-6xl font-light text-center mb-4 text-[#5C2018]"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
          data-testid="conseils-title"
        >
          Conseils & Actualités
        </h1>
        <p className="text-center text-[#332724] font-light mb-12 max-w-2xl mx-auto">
          Découvrez nos articles sur le monde de la parfumerie, nos conseils d'experts et les
          dernières tendances.
        </p>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-6 py-3 rounded-full border transition-all ${
                selectedCategory === category.id
                  ? 'bg-[#1E7A6A] text-[#FDFAF4] border-[#1E7A6A]'
                  : 'bg-white text-[#332724] border-[#B8922A]/20 hover:border-[#1E7A6A]'
              }`}
              data-testid={`category-${category.id}`}
            >
              <span className="text-sm uppercase tracking-[0.2em]">{category.name}</span>
            </button>
          ))}
        </div>

        {/* Articles Grid */}
        {loading ? (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1E7A6A] mx-auto"></div>
            <p className="mt-4 text-[#332724]">Chargement...</p>
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-[#332724] text-lg">Aucun article disponible pour le moment.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {articles.map((article) => (
              <Link
                key={article.id}
                to={`/conseils/${article.slug}`}
                className="perfume-card bg-white rounded-lg border border-gold shadow-[0_8px_32px_rgba(92, 32, 24,0.05)] overflow-hidden"
                data-testid={`article-card-${article.slug}`}
              >
                {article.image_url && (
                  <div className="h-48 overflow-hidden">
                    <img
                      src={article.image_url}
                      alt={article.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div className="p-6">
                  <p className="text-xs uppercase tracking-[0.2em] text-[#B8922A] mb-2">
                    {article.category}
                  </p>
                  <h3
                    className="text-2xl mb-3 text-[#5C2018]"
                    style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
                  >
                    {article.title}
                  </h3>
                  <p className="text-[#332724] font-light text-sm mb-4 leading-relaxed line-clamp-3">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center gap-4 text-xs text-[#332724]/70">
                    <div className="flex items-center gap-1">
                      <User size={14} />
                      <span>{article.author}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar size={14} />
                      <span>{formatDate(article.date)}</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConseilsPage;
